import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Printer, X, Tag } from 'lucide-react';

export const CreateLabelModal = ({ isOpen, onClose, docket, locations = [], onPrint }) => {
  const [docketNo, setDocketNo] = useState('');
  const [fromLocation, setFromLocation] = useState('');
  const [toLocation, setToLocation] = useState('');
  const [packages, setPackages] = useState(1);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [printSize, setPrintSize] = useState('4x6');

  // Helper to resolve an ID or raw string to a display name / code
  const resolveLocationText = (val) => {
    if (!val) return '';
    // If it looks like a UUID or ID and exists in locations list:
    const found = locations.find(l => String(l.id).toLowerCase() === String(val).toLowerCase());
    if (found) {
      return found.location_code || found.location_name || val;
    }
    return val;
  };

  useEffect(() => {
    if (docket) {
      const payload = docket.payload_json || {};
      const header = payload.header || {};
      const shipment = payload.shipment_info || {};
      const delivery = payload.delivery_info || {};
      const pkg = payload.package_details || payload.package_info || payload.cargo_details || {};
      
      const no = docket.printed_docket_no || docket.system_docket_no || '';
      
      // Look through header, shipment_info, delivery_info for From / Origin
      const rawFrom = header.from_location_name 
        || header.origin_name 
        || shipment.loading_from_name
        || delivery.loading_from_name
        || header.from_location 
        || shipment.loading_from 
        || delivery.loading_from
        || '';

      // Look through header, shipment_info, delivery_info for To / Destination
      const rawTo = header.to_location_name 
        || header.destination_name 
        || shipment.discharge_at_name
        || delivery.delivery_at_name
        || header.to_location 
        || shipment.discharge_at
        || delivery.delivery_at
        || '';
      
      const from = resolveLocationText(rawFrom);
      const to = resolveLocationText(rawTo);

      // Sum package counts if present in invoices array, else check package info
      let pkgCount = 0;
      if (Array.isArray(payload.invoices) && payload.invoices.length > 0) {
        pkgCount = payload.invoices.reduce((acc, inv) => acc + (parseInt(inv.no_of_packages, 10) || 0), 0);
      }
      if (!pkgCount) {
        pkgCount = parseInt(
          pkg.package_no_of_packages || pkg.no_of_packages || pkg.packages || payload.packages || payload.no_of_packages || 1, 
          10
        ) || 1;
      }

      setDocketNo(no);
      setFromLocation(from);
      setToLocation(to);
      setPackages(Math.max(1, pkgCount));
      setAdditionalInfo('');
      setPrintSize('4x6');
    }
  }, [docket, isOpen, locations]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onPrint) {
      onPrint({
        docketNo,
        fromLocation,
        toLocation,
        packages: Math.max(1, parseInt(packages, 10) || 1),
        additionalInfo,
        printSize
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-[720px] p-0 overflow-hidden font-ibm">
        {/* Header matching user's Image 3 */}
        <div className="border-b-2 border-emerald-600 px-6 py-4 bg-white">
          <DialogTitle className="text-sm font-bold text-gray-800 uppercase tracking-wide flex items-center gap-2">
            <Tag className="w-4 h-4 text-emerald-600" />
            <span>CREATE LABEL</span>
          </DialogTitle>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-5 bg-white">
            {/* Row 1: Docket No, From, To, No. of Packages */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-gray-700">
                  Docket No. <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={docketNo}
                  readOnly
                  className="bg-gray-100 text-gray-700 font-mono text-xs cursor-not-allowed"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs font-semibold text-gray-700">
                  From <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={fromLocation}
                  onChange={e => setFromLocation(e.target.value)}
                  placeholder="e.g. AHD-AIRPORT"
                  required
                  className="text-xs uppercase"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs font-semibold text-gray-700">
                  To <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={toLocation}
                  onChange={e => setToLocation(e.target.value)}
                  placeholder="e.g. SANAND"
                  required
                  className="text-xs uppercase"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs font-semibold text-gray-700">
                  No. of Packages <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  min="1"
                  value={packages}
                  onChange={e => setPackages(e.target.value)}
                  required
                  className="text-xs"
                />
              </div>
            </div>

            {/* Row 2: Additional Information & Print Size */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-1">
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-gray-700">
                  Additional Information
                </Label>
                <Textarea
                  value={additionalInfo}
                  onChange={e => setAdditionalInfo(e.target.value)}
                  placeholder="Enter remarks, invoice no., handling instructions..."
                  rows={4}
                  className="text-xs resize-none"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-700 block">
                  Print Size <span className="text-red-500">*</span>
                </Label>
                <div className="space-y-2 pt-1">
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-gray-800">
                    <input
                      type="radio"
                      name="printSize"
                      value="4x6"
                      checked={printSize === '4x6'}
                      onChange={() => setPrintSize('4x6')}
                      className="text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                    />
                    <span>4 × 6 (Standard Thermal Shipping Label)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-gray-800">
                    <input
                      type="radio"
                      name="printSize"
                      value="4x2"
                      checked={printSize === '4x2'}
                      onChange={() => setPrintSize('4x2')}
                      className="text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                    />
                    <span>4 × 2 (Compact Barcode Label)</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-3 bg-gray-50 border-t flex items-center justify-start gap-2">
            <Button
              type="submit"
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-ibm font-semibold h-8 px-4"
            >
              <Printer className="w-3.5 h-3.5 mr-1.5" />
              Save & Print
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="bg-amber-500 hover:bg-amber-600 text-white border-none text-xs font-ibm font-semibold h-8 px-4"
            >
              <X className="w-3.5 h-3.5 mr-1.5" />
              Cancel
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateLabelModal;
